import { ColScaffold, RowScaffold } from "../../interface";

/**
 * Converts a row-indexed selected cell map into ScaffoldSelection.selectedCells.
 *
 * @param selectedCellMap - Row-indexed selected cells.
 * @param prevRows - Current scaffold row definitions, used for stable output ordering.
 * @param prevCols - Current scaffold column definitions, used for stable output ordering.
 * @returns Selected cells array ordered by scaffold row and column order.
 */
export const toSelectedCells = <T>(
  selectedCellMap: Map<string, Set<string>>,
  prevRows: RowScaffold<T>[],
  prevCols: ColScaffold<T>[],
): { rowId: string; colId: string[] }[] => {
  const rowOrder = prevRows.map((row) => row.id);
  const colOrder = prevCols.map((col) => col.id);
  const remainingRows = Array.from(selectedCellMap.keys()).filter(
    (rowId) => !rowOrder.includes(rowId),
  );
  const orderedRowIds = [...rowOrder, ...remainingRows];

  return orderedRowIds
    .map((orderedRowId) => {
      const cols = selectedCellMap.get(orderedRowId);
      if (!cols || cols.size === 0) {
        return null;
      }

      const remainingCols = Array.from(cols).filter(
        (currentColId) => !colOrder.includes(currentColId),
      );
      const orderedColIds = [
        ...colOrder.filter((currentColId) => cols.has(currentColId)),
        ...remainingCols,
      ];
      return { rowId: orderedRowId, colId: orderedColIds };
    })
    .filter(
      (selectedRow): selectedRow is { rowId: string; colId: string[] } => selectedRow !== null,
    );
};
